import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import Box from '@mui/material/Box';
import {
  ProjectEvent_Payload_MaterialRateCreated,
  ProjectEvent_Payload_MaterialRateUpdated,
  ProjectEvent_Payload_ProjectFreightDefaultsUpdated,
  ProjectEvent_Payload_ProjectStateChanged,
  ProjectEvent_Payload_SiteRateScheduleSnapshot,
  ProjectEvent_Payload_SiteRateScheduleUpdated,
  ProjectEvent_Payload_TaxRateScheduleSaved,
  ProjectEventName,
  ProjectState,
  RateType,
  SiteRateSchedule_Read_Nested,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { startCase } from 'lodash-es';

import { EventTimelineSecondaryText } from '~components/EventTimeline/EventTimeline';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { ProjectEvent } from '~hooks/useProjects/projectEventModels';
import { getRateTypeLabel } from '~pages/Settings/RatesManagement/rateUtils';

type CurrencyFormatter = (value: number | string) => string;

const RATE_OWNERS = ['customer', 'vendor', 'driver'] as const;
type RateOwner = (typeof RATE_OWNERS)[number];

function fieldLabel(key: string): string {
  return t(`project.timeline.fields.${key}`, { defaultValue: startCase(key) });
}

function isInternalIdField(field: string): boolean {
  return field.endsWith('_id');
}

function emptyValue(): string {
  return t('project.timeline.empty_value');
}

function toNumeric(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

// Mirrors `getFormattedRateValue` from rateUtils, with timeline-specific
// empty-value handling for null/invalid amounts.
function formatRateAmount(
  value: unknown,
  type: RateType | null | undefined,
  currencyFormatter: CurrencyFormatter,
): string {
  const numeric = toNumeric(value);
  if (numeric === null) return emptyValue();
  if (type === RateType.RATE_COMMISSION) return `${numeric}%`;
  return currencyFormatter(numeric);
}

// Mirrors `formatRateLabel` from FreightRateItem: "$50.00 Per Bushel".
function formatRatePair(
  value: unknown,
  type: RateType | null | undefined,
  currencyFormatter: CurrencyFormatter,
): string {
  const hasValue = toNumeric(value) !== null;
  const hasType = Boolean(type);
  if (!hasValue && !hasType) return emptyValue();

  const typeLabel = hasType ? getRateTypeLabel(type as RateType) : '';
  if (!hasValue) return typeLabel || emptyValue();

  return `${formatRateAmount(value, type, currencyFormatter)} ${typeLabel}`.trim();
}

function formatScalarFieldValue(
  field: string,
  value: unknown,
  currencyFormatter: CurrencyFormatter,
  pairedType?: RateType,
): string {
  if (value === null || value === undefined || value === '') return emptyValue();

  if (
    field === 'buy_price' ||
    field === 'sell_price' ||
    field === 'pw_on_site_rate' ||
    field === 'total_fringe_rate'
  ) {
    return formatRateAmount(value, undefined, currencyFormatter);
  }

  // Use the paired `*_rate_type` when available so commission renders as `%`;
  // otherwise default to currency.
  if (field.endsWith('_rate_value')) {
    return formatRateAmount(value, pairedType, currencyFormatter);
  }

  if (field.endsWith('_rate_type')) {
    const label = getRateTypeLabel(value as RateType);
    return label || String(value);
  }

  if (field === 'tax_rate') return `${value}%`;

  return String(value);
}

function FromToGroup({
  before,
  after,
}: {
  before: React.ReactNode;
  after: React.ReactNode;
}) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {before}
      <ArrowForwardOutlined sx={{ fontSize: 12 }} />
      {after}
    </Box>
  );
}

function LabeledLine({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <EventTimelineSecondaryText>
      <Box component="span" sx={{ fontWeight: 600 }}>
        {label}:
      </Box>{' '}
      {children}
    </EventTimelineSecondaryText>
  );
}

function ScalarSnapshotLine({ field, value }: { field: string; value: unknown }) {
  const { currencyFormatter } = useCompanyCurrency();
  if (value === null || value === undefined || value === '') return null;
  return (
    <LabeledLine label={fieldLabel(field)}>
      {formatScalarFieldValue(field, value, currencyFormatter)}
    </LabeledLine>
  );
}

function ScalarDiffLine({
  field,
  before,
  after,
  pairedType,
}: {
  field: string;
  before: unknown;
  after: unknown;
  pairedType?: RateType;
}) {
  const { currencyFormatter } = useCompanyCurrency();
  return (
    <LabeledLine label={fieldLabel(field)}>
      <FromToGroup
        before={formatScalarFieldValue(field, before, currencyFormatter, pairedType)}
        after={formatScalarFieldValue(field, after, currencyFormatter, pairedType)}
      />
    </LabeledLine>
  );
}

function RateSnapshotLine({
  owner,
  value,
  type,
}: {
  owner: RateOwner;
  value: unknown;
  type: unknown;
}) {
  const { currencyFormatter } = useCompanyCurrency();
  if (toNumeric(value) === null && !type) return null;
  return (
    <LabeledLine label={fieldLabel(`${owner}_rate`)}>
      {formatRatePair(value, type as RateType | null | undefined, currencyFormatter)}
    </LabeledLine>
  );
}

function RateDiffLine({
  owner,
  beforeValue,
  beforeType,
  afterValue,
  afterType,
}: {
  owner: RateOwner;
  beforeValue: unknown;
  beforeType: unknown;
  afterValue: unknown;
  afterType: unknown;
}) {
  const { currencyFormatter } = useCompanyCurrency();
  return (
    <LabeledLine label={fieldLabel(`${owner}_rate`)}>
      <FromToGroup
        before={formatRatePair(
          beforeValue,
          beforeType as RateType | null | undefined,
          currencyFormatter,
        )}
        after={formatRatePair(
          afterValue,
          afterType as RateType | null | undefined,
          currencyFormatter,
        )}
      />
    </LabeledLine>
  );
}

function formatProjectState(value: ProjectState | null | undefined): string {
  if (!value) return emptyValue();
  return t(`status.${value}`, { defaultValue: startCase(value) });
}

function StateChangeDetail({
  payload,
}: {
  payload: ProjectEvent_Payload_ProjectStateChanged;
}) {
  return (
    <EventTimelineSecondaryText>
      <FromToGroup
        before={formatProjectState(payload.from_state)}
        after={formatProjectState(payload.to_state)}
      />
    </EventTimelineSecondaryText>
  );
}

function ChangesDetail({
  changes,
}: {
  changes: Record<string, { before: string | null; after: string | null }>;
}) {
  const entries = Object.entries(changes).filter(([field]) => !isInternalIdField(field));
  if (!entries.length) return null;
  return (
    <>
      {entries.map(([field, change]) => {
        const owner = field.match(/^(.+)_rate_value$/)?.[1];
        const pairedType = owner
          ? (changes[`${owner}_rate_type`]?.after as RateType | undefined)
          : undefined;
        return (
          <ScalarDiffLine
            key={field}
            field={field}
            before={change.before}
            after={change.after}
            pairedType={pairedType}
          />
        );
      })}
    </>
  );
}

function FreightDefaultsDetail({
  payload,
}: {
  payload: ProjectEvent_Payload_ProjectFreightDefaultsUpdated;
}) {
  const { before, after } = payload;
  const owners = RATE_OWNERS.filter((owner) => {
    const valueKey = `${owner}_rate_value` as const;
    const typeKey = `${owner}_rate_type` as const;
    return before[valueKey] !== after[valueKey] || before[typeKey] !== after[typeKey];
  });
  if (!owners.length) return null;
  return (
    <>
      {owners.map((owner) => (
        <RateDiffLine
          key={owner}
          owner={owner}
          beforeValue={before[`${owner}_rate_value`]}
          beforeType={before[`${owner}_rate_type`]}
          afterValue={after[`${owner}_rate_value`]}
          afterType={after[`${owner}_rate_type`]}
        />
      ))}
    </>
  );
}

const MATERIAL_RATE_SNAPSHOT_FIELDS: Array<
  keyof ProjectEvent_Payload_MaterialRateCreated
> = ['buy_price', 'sell_price', 'unit_of_measure'];

const CONDITION_FIELDS = [
  'pickup_site',
  'dropoff_site',
  'material',
  'equipment_type',
] as const satisfies ReadonlyArray<
  Exclude<keyof SiteRateSchedule_Read_Nested, 'id' | 'name'>
>;

// Returns a one-line label that identifies which site rate schedule the event
// refers to: the schedule's own name when set, otherwise a condition summary
// built from the resolved condition names. Falls back to `legacyName` (the
// top-level `payload.name` on pre-summary snapshot events) when no nested
// summary is present. Returns null when nothing identifying is available.
export function getSiteRateScheduleSubject(
  summary: SiteRateSchedule_Read_Nested | null | undefined,
  legacyName?: string | null,
): string | null {
  if (summary?.name) return summary.name;

  if (summary) {
    const parts = CONDITION_FIELDS.flatMap((field) => {
      const name = summary[field]?.name;
      return name ? [name] : [];
    });
    if (parts.length > 0) return parts.join(' · ');
  }

  return legacyName ?? null;
}

function SiteRateScheduleIdentityHeader({
  summary,
  legacyName,
}: {
  summary: SiteRateSchedule_Read_Nested | null | undefined;
  legacyName?: string | null;
}) {
  // Legacy snapshot events recorded before the BE summary field was added
  // carry `name` at the top level but no nested object. Surface that name so
  // historical entries don't lose their schedule identity.
  if (!summary) {
    return legacyName ? (
      <LabeledLine label={fieldLabel('name')}>{legacyName}</LabeledLine>
    ) : null;
  }

  const conditions = CONDITION_FIELDS.flatMap((field) => {
    const name = summary[field]?.name;
    if (!name) return [];
    return [
      <Box component="span" key={field}>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {fieldLabel(field)}:
        </Box>{' '}
        {name}
      </Box>,
    ];
  });

  // The schedule name renders on the primary timeline line via
  // getSiteRateScheduleSubject, so we don't repeat it here. We only fall back
  // to showing it in the detail strip when no conditions are set — otherwise
  // the conditions strip alone provides the secondary context.
  if (conditions.length === 0) {
    return summary.name ? (
      <LabeledLine label={fieldLabel('name')}>{summary.name}</LabeledLine>
    ) : null;
  }

  return (
    <EventTimelineSecondaryText>
      <Box component="span" sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.5 }}>
        {conditions.map((node, index) => (
          <Box component="span" key={index} sx={{ display: 'inline-flex' }}>
            {index > 0 && (
              <Box component="span" sx={{ mr: 0.5 }}>
                ·
              </Box>
            )}
            {node}
          </Box>
        ))}
      </Box>
    </EventTimelineSecondaryText>
  );
}

function SiteRateScheduleSnapshotDetail({
  payload,
}: {
  payload: ProjectEvent_Payload_SiteRateScheduleSnapshot;
}) {
  return (
    <>
      <SiteRateScheduleIdentityHeader
        summary={payload.site_rate_schedule}
        legacyName={payload.name}
      />
      {RATE_OWNERS.map((owner) => (
        <RateSnapshotLine
          key={owner}
          owner={owner}
          value={payload[`${owner}_rate_value`]}
          type={payload[`${owner}_rate_type`]}
        />
      ))}
    </>
  );
}

function MaterialRateCreatedDetail({
  payload,
}: {
  payload: ProjectEvent_Payload_MaterialRateCreated;
}) {
  return (
    <>
      {MATERIAL_RATE_SNAPSHOT_FIELDS.map((field) => (
        <ScalarSnapshotLine key={field} field={field} value={payload[field]} />
      ))}
    </>
  );
}

function MaterialRateUpdatedDetail({
  payload,
}: {
  payload: ProjectEvent_Payload_MaterialRateUpdated;
}) {
  return (
    <>
      <ScalarSnapshotLine field="movement" value={payload.movement} />
      <ChangesDetail changes={payload.changes} />
    </>
  );
}

function TaxRateScheduleDetail({
  payload,
}: {
  payload: ProjectEvent_Payload_TaxRateScheduleSaved;
}) {
  return (
    <>
      <ScalarSnapshotLine
        field="tax_rate_schedule_name"
        value={payload.tax_rate_schedule_name}
      />
      <ScalarSnapshotLine field="tax_rate" value={payload.tax_rate} />
    </>
  );
}

interface ProjectTimelineEventDetailProps {
  event: ProjectEvent;
}

export default function ProjectTimelineEventDetail({
  event,
}: ProjectTimelineEventDetailProps) {
  const payload = event.payload;
  if (!payload) return null;

  switch (event.eventName) {
    case ProjectEventName.PROJECT_STATE_CHANGED:
      return (
        <StateChangeDetail
          payload={payload as ProjectEvent_Payload_ProjectStateChanged}
        />
      );
    case ProjectEventName.MATERIAL_RATE_CREATED:
      return (
        <MaterialRateCreatedDetail
          payload={payload as ProjectEvent_Payload_MaterialRateCreated}
        />
      );
    case ProjectEventName.MATERIAL_RATE_UPDATED:
      return (
        <MaterialRateUpdatedDetail
          payload={payload as ProjectEvent_Payload_MaterialRateUpdated}
        />
      );
    case ProjectEventName.SITE_RATE_SCHEDULE_UPDATED: {
      const updatedPayload = payload as ProjectEvent_Payload_SiteRateScheduleUpdated;
      return (
        <>
          <SiteRateScheduleIdentityHeader summary={updatedPayload.site_rate_schedule} />
          <ChangesDetail changes={updatedPayload.changes} />
        </>
      );
    }
    case ProjectEventName.SITE_RATE_SCHEDULE_CREATED:
    case ProjectEventName.SITE_RATE_SCHEDULE_DELETED:
      return (
        <SiteRateScheduleSnapshotDetail
          payload={payload as ProjectEvent_Payload_SiteRateScheduleSnapshot}
        />
      );
    case ProjectEventName.PROJECT_FREIGHT_DEFAULTS_UPDATED:
      return (
        <FreightDefaultsDetail
          payload={payload as ProjectEvent_Payload_ProjectFreightDefaultsUpdated}
        />
      );
    case ProjectEventName.TAX_RATE_SCHEDULE_SAVED:
      return (
        <TaxRateScheduleDetail
          payload={payload as ProjectEvent_Payload_TaxRateScheduleSaved}
        />
      );
    default:
      return null;
  }
}
